import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import {
  CheckCircle2,
  XCircle,
  ArrowRight,
  ShieldCheck,
  Newspaper,
  Target,
  AlertTriangle,
  TrendingUp,
} from 'lucide-react';

type Vendor = {
  name: string;
  category: string;
  bestFor: string;
  strengths: string[];
  watchOuts: string[];
  pricing: string;
  verdict: string;
};

const vendors: Vendor[] = [
  {
    name: 'NoQ5',
    category: 'Autonomous CRM + evidence-based forecast',
    bestFor:
      'CROs who want the forecast grounded in real buyer activity, not rep memory.',
    strengths: [
      'Captures every call, email and meeting automatically',
      'Drafts the CRM update; rep approves in one tap',
      'Writes back to HubSpot, Salesforce and Pipedrive natively',
      'Forecast scored against historical closed-won cohorts',
      'EU-hosted, AES-256-GCM, full audit trail, ISO 27001 in progress',
    ],
    watchOuts: [
      'Single tier at €59 / user / month, annual only',
      'Microsoft 365 still in beta',
    ],
    pricing: '€59 / user / month, annual',
    verdict:
      'The execution layer. The forecast comes from what buyers actually did, not from what reps remembered to type.',
  },
  {
    name: 'Aviso',
    category: 'Forecast and revenue intelligence',
    bestFor:
      'Enterprise teams with a dedicated RevOps function and an existing Clari-class budget.',
    strengths: [
      'Mature forecast roll-up across business units',
      'Recently announced a generative AI agent positioned as a Chief of Staff for Revenue and GTM',
    ],
    watchOuts: [
      'Activity capture still relies heavily on rep input',
      'Pricing opaque, enterprise sales cycle',
      'New AI agent is a copilot positioning, not an autonomous execution layer',
    ],
    pricing: 'Enterprise, quote-based',
    verdict:
      'Strong forecasting roll-up for very large orgs. The "Chief of Staff" framing is new; the underlying capture model still depends on the rep.',
  },
  {
    name: 'BoostUp',
    category: 'Revenue intelligence',
    bestFor:
      'Mid-market and enterprise revenue teams that want a forecasting cockpit on top of Salesforce.',
    strengths: [
      'Configurable forecast roll-ups and deal scoring',
      'Useful inspection workflows for managers',
    ],
    watchOuts: [
      'Salesforce-centric; weaker across multi-CRM estates',
      'Still requires upstream capture to be solved separately',
    ],
    pricing: 'Enterprise, quote-based',
    verdict:
      'A solid forecasting and inspection layer for Salesforce-heavy orgs. Does not solve CRM hygiene at the source.',
  },
  {
    name: 'Salesforce Forecasting (Einstein)',
    category: 'Native CRM forecast',
    bestFor: 'Salesforce-only teams using opportunity stages as the source of truth.',
    strengths: [
      'Native to Salesforce, no extra contract',
      'Einstein-assisted opportunity scoring',
    ],
    watchOuts: [
      'Forecast quality is bounded by Salesforce data quality',
      'Locked to Salesforce only',
    ],
    pricing: 'Bundled with Sales Cloud tiers',
    verdict:
      'Free with Salesforce. Garbage in, garbage out — your forecast is only as good as the data reps type.',
  },
  {
    name: 'HubSpot Forecasting',
    category: 'Native CRM forecast',
    bestFor: 'SMB and mid-market teams running revenue on HubSpot.',
    strengths: [
      'Bundled with Sales Hub Professional and Enterprise',
      'Simple weighted and category-based forecasts',
    ],
    watchOuts: [
      'Locked to HubSpot only',
      'Limited evidence layer behind the number',
    ],
    pricing: 'Bundled with Sales Hub Pro / Enterprise',
    verdict:
      'Adequate for HubSpot-only teams. Not designed to defend a board-grade forecast.',
  },
  {
    name: 'Pipedrive Forecasting',
    category: 'Native CRM forecast',
    bestFor: 'SMB sales teams already living inside Pipedrive.',
    strengths: [
      'Lightweight pipeline value and probability views',
      'No additional contract',
    ],
    watchOuts: [
      'Pipedrive only',
      'No evidence layer or AI-driven risk scoring',
    ],
    pricing: 'Included with Pipedrive tiers',
    verdict:
      'Useful for SMB pipeline visibility. Not a forecasting platform.',
  },
];

const BestSalesForecastingSoftware2026 = () => {
  const url = 'https://noq5.ai/best-sales-forecasting-software-2026';
  const title = 'Best Sales Forecasting Software 2026 | Honest Comparison';
  const description =
    'Independent 2026 comparison of sales forecasting software: NoQ5, Aviso, BoostUp, Salesforce, HubSpot and Pipedrive. Evidence-based forecasting for CROs.';

  return (
    <Layout>
      <SEOHead
        title={title}
        description={description}
        canonicalUrl={url}
        type="article"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: title,
            description,
            author: { '@type': 'Organization', name: 'NoQ5' },
            publisher: {
              '@type': 'Organization',
              name: 'NoQ5',
              url: 'https://noq5.ai',
              logo: {
                '@type': 'ImageObject',
                url: 'https://noq5.ai/noq5-og-image.png',
              },
            },
            datePublished: '2026-06-26',
            dateModified: '2026-06-26',
            mainEntityOfPage: url,
            mentions: [
              { '@type': 'SoftwareApplication', name: 'Aviso' },
              { '@type': 'SoftwareApplication', name: 'BoostUp' },
              { '@type': 'SoftwareApplication', name: 'Salesforce' },
              { '@type': 'SoftwareApplication', name: 'HubSpot' },
              { '@type': 'SoftwareApplication', name: 'Pipedrive' },
            ],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://noq5.ai/' },
              { '@type': 'ListItem', position: 2, name: 'Best Sales Forecasting Software 2026', item: url },
            ],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'What is the best sales forecasting software in 2026?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'There is no single answer because the right tool depends on what is broken. For CROs who need the forecast grounded in real buyer activity across HubSpot, Salesforce or Pipedrive, NoQ5 captures and writes the evidence back automatically. For very large Salesforce-only enterprises, Aviso and BoostUp run mature roll-ups. HubSpot, Salesforce and Pipedrive bundled forecasts are adequate when the team is single-CRM and accepts the data-quality ceiling.',
                },
              },
              {
                '@type': 'Question',
                name: 'How is NoQ5 different from Aviso?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Aviso recently positioned a generative AI agent as a Chief of Staff for revenue and GTM teams. NoQ5 is an autonomous CRM execution layer purpose-built around that role: it captures activity, drafts the CRM update, the rep approves in one tap, and the platform writes back to HubSpot, Salesforce or Pipedrive. Aviso is a forecast roll-up tool with an AI copilot bolted on; NoQ5 is an execution layer the forecast is built on.',
                },
              },
              {
                '@type': 'Question',
                name: 'Can I keep Salesforce or HubSpot forecasting and add NoQ5?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes. Most teams keep the native forecast view inside the CRM and add NoQ5 as the execution layer underneath. NoQ5 improves the data the native forecast is computed on, then adds an evidence-based deal score and pipeline brief on top.',
                },
              },
              {
                '@type': 'Question',
                name: 'Is NoQ5 GDPR-compliant and EU-hosted?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes. Data is hosted in the EU, encrypted at rest with AES-256-GCM and in transit with TLS 1.3. ISO 27001 is in progress, the sub-processor list is published, and every CRM write is captured in an immutable audit trail.',
                },
              },
              {
                '@type': 'Question',
                name: 'How much does NoQ5 cost?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Single tier at €59 per user per month, billed annually. No usage tiers, no surprise overages.',
                },
              },
            ],
          },
        ]}
      />

      <main className="bg-background text-foreground">
        {/* Hero */}
        <section className="container mx-auto px-4 pt-24 pb-12 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <p className="text-sm font-medium text-primary mb-3">Buyer's Guide · 2026</p>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">
              Best Sales Forecasting Software of 2026
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl leading-relaxed">
              An independent, opinionated comparison of six platforms — NoQ5, Aviso, BoostUp,
              Salesforce, HubSpot and Pipedrive — written for revenue leaders who care more about
              forecast accuracy than about who has the loudest AI announcement.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/?scrollTo=demo">
                <Button size="lg" className="gap-2">
                  Book a demo <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/sales-forecasting-software">
                <Button size="lg" variant="outline">
                  Read the full buyer's guide
                </Button>
              </Link>
            </div>
          </motion.div>
        </section>

        {/* News-jack: Aviso Chief of Staff announcement */}
        <section className="container mx-auto px-4 py-12 max-w-5xl">
          <div className="bg-card border border-border rounded-2xl p-6 md:p-8 shadow-subtle">
            <div className="flex items-start gap-3 mb-4">
              <Newspaper className="w-5 h-5 text-primary shrink-0 mt-1" />
              <p className="text-sm font-medium text-primary">The news that triggered this guide</p>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Aviso unveiled a generative AI "Chief of Staff for Revenue and GTM." Here is what
              actually changed.
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              The announcement reframes Aviso from a forecasting tool to an AI Chief of Staff —
              the same positioning NoQ5 has owned since launch. Naming the role is helpful for the
              category. It does not change the underlying mechanic: Aviso's data still depends on
              what reps log into Salesforce. NoQ5 captures the activity at source, drafts the CRM
              update, and writes it back after one-tap approval.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              The real question for a CRO in 2026 is not "does my forecast tool have an AI agent."
              Every vendor will have one by Q4. The question is whether the agent removes the
              admin work the rep currently does, or whether it sits on top of the same broken
              capture layer and produces faster opinions about the same incomplete data.
            </p>
          </div>
        </section>

        {/* Comparison cards */}
        <section className="container mx-auto px-4 py-16 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">
            The six platforms, scored honestly
          </h2>
          <p className="text-muted-foreground mb-10 max-w-3xl">
            Each card lists the category, who the platform is built for, what it does well, where
            it struggles, and our verdict. Pricing is shown where it is published.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {vendors.map((v) => (
              <div
                key={v.name}
                className={`rounded-2xl border p-6 shadow-subtle bg-card ${
                  v.name === 'NoQ5' ? 'border-primary/40 ring-1 ring-primary/20' : 'border-border'
                }`}
              >
                <div className="flex items-baseline justify-between mb-2">
                  <h3 className="text-xl font-bold">{v.name}</h3>
                  {v.name === 'NoQ5' && (
                    <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                      Our pick
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-3">{v.category}</p>
                <p className="text-sm mb-4">
                  <span className="font-semibold">Best for:</span> {v.bestFor}
                </p>

                <p className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary" /> Strengths
                </p>
                <ul className="text-sm text-muted-foreground space-y-1 mb-4 pl-6 list-disc">
                  {v.strengths.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>

                <p className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" /> Watch-outs
                </p>
                <ul className="text-sm text-muted-foreground space-y-1 mb-4 pl-6 list-disc">
                  {v.watchOuts.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>

                <div className="border-t border-border pt-4 mt-4">
                  <p className="text-sm">
                    <span className="font-semibold">Pricing:</span>{' '}
                    <span className="text-muted-foreground">{v.pricing}</span>
                  </p>
                  <p className="text-sm mt-2">
                    <span className="font-semibold">Verdict:</span>{' '}
                    <span className="text-muted-foreground">{v.verdict}</span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Buyer's checklist */}
        <section className="container mx-auto px-4 py-16 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
            The five questions to ask any forecasting vendor in 2026
          </h2>
          <div className="space-y-4">
            {[
              {
                icon: Target,
                q: 'Does the forecast number trace back to specific buyer signals?',
                a: 'If the only evidence is a stage change or a rep commit, the forecast is a guess in a spreadsheet.',
              },
              {
                icon: TrendingUp,
                q: 'Does the platform write back to the CRM without rep typing?',
                a: 'Forecasts get worse every week reps skip data entry. Capture and write-back must be automatic.',
              },
              {
                icon: ShieldCheck,
                q: 'Is data hosted in the EU and encrypted at rest with documented residency?',
                a: 'European CROs cannot defend US-hosted forecasting tools in a 2026 procurement review.',
              },
              {
                icon: CheckCircle2,
                q: 'Does it work across HubSpot, Salesforce and Pipedrive, or just one?',
                a: 'Most growth-stage companies run more than one CRM. Single-CRM forecasting tools cannot consolidate.',
              },
              {
                icon: XCircle,
                q: 'Is pricing transparent and per-seat, with no usage-based surprises?',
                a: 'Usage-priced AI tools become unpredictable line items. Per-seat keeps the budget defensible.',
              },
            ].map(({ icon: Icon, q, a }) => (
              <div key={q} className="bg-card border border-border rounded-xl p-5 flex gap-4">
                <Icon className="w-5 h-5 text-primary shrink-0 mt-1" />
                <div>
                  <p className="font-semibold mb-1">{q}</p>
                  <p className="text-sm text-muted-foreground">{a}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="container mx-auto px-4 py-16 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-8">
            Frequently asked
          </h2>
          <div className="space-y-4">
            {[
              {
                q: 'What is the best sales forecasting software in 2026?',
                a: 'It depends on what is broken. If the forecast is wrong because reps do not log activity, NoQ5 fixes the capture layer and the forecast at the same time. If forecast roll-up across business units is the problem, Aviso and BoostUp are strong. If the team is single-CRM and small, the native HubSpot, Salesforce or Pipedrive forecast is usually enough.',
              },
              {
                q: 'How is NoQ5 different from Aviso?',
                a: 'Aviso recently rebranded its AI as a Chief of Staff for revenue and GTM. NoQ5 was built around that role from day one: autonomous capture from calls, emails and meetings, one-tap approval, automatic write-back to HubSpot, Salesforce and Pipedrive. Aviso assumes the CRM is already clean; NoQ5 makes it clean.',
              },
              {
                q: 'Can I keep Salesforce or HubSpot forecasting and add NoQ5?',
                a: 'Yes. Most customers keep the native CRM forecast view and use NoQ5 underneath as the execution layer that fills in the activity, evidence and risk signals the native forecast is computed on.',
              },
              {
                q: 'Is NoQ5 GDPR-compliant and EU-hosted?',
                a: 'Yes. EU-hosted, AES-256-GCM at rest, TLS 1.3 in transit, ISO 27001 in progress, published sub-processor list and full audit trail on every CRM write.',
              },
              {
                q: 'How much does NoQ5 cost?',
                a: 'A single tier at €59 per user per month, billed annually. No usage tiers, no overage line items.',
              },
            ].map((f) => (
              <details
                key={f.q}
                className="bg-card border border-border rounded-xl p-5 group"
              >
                <summary className="font-semibold cursor-pointer list-none flex justify-between items-center">
                  {f.q}
                  <span className="text-primary group-open:rotate-45 transition-transform text-2xl leading-none">
                    +
                  </span>
                </summary>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Closing CTA */}
        <section className="container mx-auto px-4 py-20 max-w-4xl text-center">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-6">
            See the forecast NoQ5 builds from your own pipeline
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            A 30-minute working session on your real HubSpot, Salesforce or Pipedrive instance.
            You will see the evidence under every deal and the forecast NoQ5 produces from it.
          </p>
          <Link to="/?scrollTo=demo">
            <Button size="lg" className="gap-2">
              Book a demo <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
          <p className="text-sm text-muted-foreground mt-6">
            Related reading:{' '}
            <Link to="/sales-forecasting-software" className="text-primary hover:underline">
              The CRO's full buyer's guide
            </Link>{' '}
            ·{' '}
            <Link to="/revenue-intelligence" className="text-primary hover:underline">
              Revenue intelligence platform
            </Link>{' '}
            ·{' '}
            <Link to="/best-ai-sales-assistants-2026" className="text-primary hover:underline">
              Best AI sales assistants 2026
            </Link>
          </p>
        </section>
      </main>
    </Layout>
  );
};

export default BestSalesForecastingSoftware2026;
